import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { parseLocalDate } from '../utils/dates';
import { useFacility } from '../contexts/FacilityContext';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import CMS1500Form from './CMS1500Form';
import CMS1450Form from './CMS1450Form';
import ClinicalNotePanel from '../components/ClinicalNotePanel';
import { fetchWorklistItem } from '../services/api';
import { facilityDefaultForm as resolveFacilityDefaultForm } from '../utils/billing';
import './GenerateClaim.css';

function GenerateClaim() {
  const { selectedFacility } = useFacility();
  const location = useLocation();
  const navigate = useNavigate();
  const baseClaim     = location.state?.claim        || null;
  const isEdit        = location.state?.isEdit        || false;
  const returnTo      = location.state?.returnTo      || '/worklist';
  const cobMode       = location.state?.cobMode       || false;
  const cobOrder      = location.state?.cobOrder      || null;
  const cobPayer      = location.state?.cobPayer      || null;
  const cobPriorRemit = location.state?.cobPriorRemit || null;

  const [fullClaim, setFullClaim]       = useState(null);
  const [detailLoading, setDetailLoading] = useState(Boolean(baseClaim?.id));
  const [detailError, setDetailError]   = useState(null);

  // Fetch full encounter details (diagnoses, service lines, patient, insurance)
  // before rendering the form so all pre-populated fields are ready on first paint.
  useEffect(() => {
    if (!baseClaim?.id) { setDetailLoading(false); return; }

    fetchWorklistItem(baseClaim.id)
      .then((res) => setFullClaim(res.data))
      .catch(() => {
        // Non-fatal — fall back to the basic worklist row so the form
        // still opens, just without full pre-population.
        setDetailError('Could not load full encounter details. Some fields may not be pre-filled.');
        setFullClaim(baseClaim);
      })
      .finally(() => setDetailLoading(false));
  }, [baseClaim?.id]);

  // Use the enriched claim when available, fall back to the basic row
  const claim = fullClaim || baseClaim;

  const billingType = selectedFacility?.billingType || 'Professional';

  // Which form to render comes from the SERVER (GET /worklist/:id → resolved_form_type),
  // which resolves it with the exact rule /generate parses the submission with: the claim's
  // stored form_type, switched only by the destination payer's accepted-forms rule. Deriving
  // it here from the selected facility's default let the two disagree — the form posted one
  // shape, the server read it as the other, and the claim's lines and diagnoses were dropped.
  // A COB secondary/tertiary bills the payer at that COB level, so it reads that slot's
  // resolution. If the detail fetch failed, the claim's stored form is the best guess (the
  // server refuses a mismatched submission with 409 rather than saving it).
  const cobSlot = cobMode && cobOrder > 1 ? claim?.patient?.insurances?.[cobOrder - 1] : null;
  const resolution = cobSlot || claim;
  const storedForm = claim?.form_type || resolveFacilityDefaultForm(billingType);
  const resolvedForm = resolution?.resolved_form_type || storedForm;
  const formSwitched = Boolean(resolution?.form_switched) && resolvedForm !== storedForm;
  const formResolutionError = resolution?.form_resolution_error || null;
  const isCMS1500 = resolvedForm === 'CMS-1500';
  const formLabel = isCMS1500 ? 'CMS-1500' : 'CMS-1450 (UB-04)';

  const ORDER_LABELS = { 2: 'Secondary', 3: 'Tertiary' };
  const backLabel = isEdit || cobMode ? 'Back to Claim Details' : 'Back to Worklist';
  const pageTitle = cobMode && cobOrder > 1
    ? `Generate ${ORDER_LABELS[cobOrder] || 'Secondary'} Claim`
    : isEdit ? 'Update Claim' : 'Generate Claim';

  const handleCancel = () => {
    navigate(returnTo);
  };

  if (!selectedFacility) {
    return (
      <div className="gc-page">
        <div className="gc-no-facility">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <h2>No Facility Selected</h2>
          <p>Select a facility first to generate a claim.</p>
          <Link to="/" className="wl-select-btn">Select a Facility</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="gc-page">
      <ActiveFacilityBanner />

      {/* Back link */}
      <Link to={returnTo} className="gc-back-link">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        {backLabel}
      </Link>

      {/* Header */}
      <div className="gc-header">
        <div className="gc-header-left">
          <h1 className="gc-title">{pageTitle}</h1>
          <p className="gc-subtitle">
            {selectedFacility.name} — {billingType} Billing
          </p>
        </div>
        <span className={`gc-form-badge ${isCMS1500 ? 'gc-badge-1500' : 'gc-badge-1450'}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          {formLabel}
        </span>
      </div>

      {/* Encounter Info Bar */}
      {claim && (
        <div className="gc-encounter-bar">
          <div className="gc-encounter-item">
            <span className="gc-encounter-label">Encounter</span>
            <span className="gc-encounter-value">{claim.encounter_id}</span>
          </div>
          <div className="gc-encounter-divider" />
          <div className="gc-encounter-item">
            <span className="gc-encounter-label">Patient</span>
            <span className="gc-encounter-value">{claim.patient_name}</span>
          </div>
          <div className="gc-encounter-divider" />
          <div className="gc-encounter-item">
            <span className="gc-encounter-label">Provider</span>
            <span className="gc-encounter-value">{claim.provider}</span>
          </div>
          <div className="gc-encounter-divider" />
          <div className="gc-encounter-item">
            <span className="gc-encounter-label">Service Date</span>
            <span className="gc-encounter-value">
              {parseLocalDate(claim.service_date).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
          <div className="gc-encounter-divider" />
          <div className="gc-encounter-item">
            <span className="gc-encounter-label">Amount</span>
            <span className="gc-encounter-value">
              ${claim.amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* COB banner — shown when generating secondary or tertiary claim */}
      {cobMode && cobOrder > 1 && (
        <div className="gc-cob-banner">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          <strong>COB — {ORDER_LABELS[cobOrder]} Claim</strong>
          {cobPayer && (
            <>
              <span className="gc-cob-banner-divider" />
              <span>Payer: {cobPayer.payer_name || cobPayer.insurance_provider}</span>
            </>
          )}
          {cobPriorRemit && (
            <>
              <span className="gc-cob-banner-divider" />
              <span>
                Balance forwarded: $
                {(parseFloat(cobPriorRemit.allowed_amount || 0) - parseFloat(cobPriorRemit.paid_amount || 0))
                  .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </>
          )}
        </div>
      )}

      {/* Clinical note — shown once detail is loaded; billers use this to enter codes.

          🔴 HIDDEN ON A SYNCED CLAIM (plan Step 8, D28). The clinical note left the
          encounter contract and RCM storage entirely, so `clinical_note` is null on
          every sync-created claim and this panel would render empty on all of them.

          That is a WORKFLOW CHANGE FOR BILLERS, not only a schema change, and an
          empty panel communicates it badly: a coder assigning an E/M level now
          reads the documentation IN THE SOURCE SYSTEM — the system of record,
          under its access controls and its audit trail — rather than in a second
          copy the RCM has no billing use for.

          Manual claims are unchanged: they keep their note and this panel. */}
      {!detailLoading && !claim?.source_system && claim?.clinical_note && (
        <ClinicalNotePanel note={claim.clinical_note} />
      )}

      {/* Loading / error state while fetching full encounter details */}
      {detailLoading && (
        <div className="gc-detail-loading">
          <span className="gc-detail-spinner" />
          Loading encounter details…
        </div>
      )}
      {detailError && !detailLoading && (
        <div className="gc-detail-warning">{detailError}</div>
      )}

      {/* Payer accepts no form this claim can switch to — /generate will refuse it */}
      {!detailLoading && formResolutionError && (
        <div className="gc-detail-warning">{formResolutionError}</div>
      )}

      {/* Payer form-switch banner — this claim's stored form is rejected by this payer */}
      {!detailLoading && formSwitched && (
        <div className="gc-form-switch-banner">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span>
            This payer does not accept <strong>{storedForm}</strong> (this claim&apos;s form).
            Billing as <strong>{resolvedForm}</strong> per the payer&apos;s accepted-forms rule.
            Confirm the defaulted place of service, diagnosis pointers, and rendering provider before submitting.
          </span>
        </div>
      )}

      {/* Form — only render once the detail fetch has resolved */}
      {!detailLoading && (
        isCMS1500 ? (
          <CMS1500Form
            claim={claim}
            facility={selectedFacility}
            switchedFromInstitutional={formSwitched && storedForm === 'CMS-1450'}
            onCancel={handleCancel}
            isEdit={isEdit}
            returnTo={returnTo}
            cobMode={cobMode}
            cobOrder={cobOrder}
            cobPayer={cobPayer}
            cobPriorRemit={cobPriorRemit}
          />
        ) : (
          <CMS1450Form
            claim={claim}
            facility={selectedFacility}
            onCancel={handleCancel}
            isEdit={isEdit}
            returnTo={returnTo}
            cobMode={cobMode}
            cobOrder={cobOrder}
            cobPayer={cobPayer}
            cobPriorRemit={cobPriorRemit}
          />
        )
      )}
    </div>
  );
}

export default GenerateClaim;
